import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useQuery, useQueryClient} from 'react-query';
import {useTranslation} from 'react-i18next';
import {Box, Button, Divider, Typography, useMediaQuery} from '@mui/material';
import {LoadingButton} from '@mui/lab';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import {enqueueSnackbar} from 'notistack';
import EditCanvasView from './EditCanvasView';
import {ResourceService} from 'crqlar-react-common/dist/services/resource/resource.service';
import {Resource} from 'crqlar-react-common/dist/models/resource/resource.model';
import {FloorplanService} from 'crqlar-react-common/dist/services/floorplan/floorplan.service';
import {ServiceService} from 'crqlar-react-common/dist/services/service/service.service';
import {Service} from 'crqlar-react-common/dist/models/service/service.model';
import {selectRestaurantToFloorPlanElements, selectTablesToFloorPlanItems} from "./utils/migration-utils";
import {serializeElement, serializeTableResource} from "./utils/fabric-serializers";
import LoadingSkeleton from "./shared/LoadingSkeleton";
import {useFloorplanContext} from "./hooks/useFloorplanContext";
import {FloorplanContextProvider} from "./FloorplanContextProvider";
import {getStoredTableGroups, syncTableGroupsWithTables, TableGroupTableRef} from "./utils/table-groups";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";
import {useUnsavedChangesWarning} from "crqlar-react-common/dist/utils/useUnsavedChangesWarning";
import UnsavedChangesDialog from "crqlar-react-common/dist/components/UnsavedChangesDialog";

interface Props {
    restaurantId: string;
    onSaved: () => void;
    onBack: () => void;
}

const HEADER_HEIGHT = 56;

interface EditCanvasHeaderProps {
    onBack: () => void;
    restaurantName?: string;
    showActions?: boolean;
    onSaveOnly?: () => void;
    onSaveAndClose?: () => void;
    activeSaveAction?: 'save' | 'saveAndClose' | null;
    isSaving?: boolean;
    readOnly?: boolean;
    isDirty?: boolean;
}

const EditCanvasHeader: React.FC<EditCanvasHeaderProps> = ({
    onBack,
    restaurantName,
    showActions = false,
    onSaveOnly,
    onSaveAndClose,
    activeSaveAction = null,
    isSaving = false,
    readOnly = false,
    isDirty = false,
}) => {
    const {t} = useTranslation();

    return (
        <Box
            sx={{
                height: {xs: showActions ? 104 : HEADER_HEIGHT, md: HEADER_HEIGHT},
                flexShrink: 0,
                display: "flex",
                flexDirection: {xs: "column", md: "row"},
                justifyContent: "space-between",
                alignItems: {xs: "stretch", md: "center"},
            }}
        >
            <Box sx={{height: HEADER_HEIGHT, minWidth: 0, display: 'flex', alignItems: 'center'}}>
                <Button
                    variant="text"
                    onClick={onBack}
                    startIcon={<ChevronLeftIcon/>}
                    sx={{flexShrink: 0}}
                >
                    {t('Back')}
                </Button>

                <Divider orientation="vertical" flexItem sx={{my: 1.5, mx: {xs: 1, md: 1.5}}}/>

                <Box sx={{minWidth: 0, display: 'flex', alignItems: 'baseline', gap: 1}}>
                    <Typography variant="h6" fontWeight={600} lineHeight={1.2} noWrap>
                        {t('Floorplan settings')}
                    </Typography>
                    {restaurantName && (
                        <Typography variant="body2" color="text.secondary" noWrap>
                            {restaurantName}
                        </Typography>
                    )}
                </Box>
            </Box>

            {showActions ? (
                <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1}}>
                    <LoadingButton
                        variant="text"
                        onClick={onSaveOnly}
                        loading={activeSaveAction === 'save' && isSaving}
                        disabled={isSaving || readOnly || !isDirty}
                        color={'secondary'}
                    >
                        {t('Save')}
                    </LoadingButton>

                    <LoadingButton
                        variant="contained"
                        onClick={onSaveAndClose}
                        loading={activeSaveAction === 'saveAndClose' && isSaving}
                        disabled={isSaving || readOnly || !isDirty}
                        color={'secondary'}
                    >
                        {t('Save changes & close')}
                    </LoadingButton>
                </Box>
            ) : (
                <Box sx={{width: 1, maxWidth: 0}}/>
            )}
        </Box>
    );
};

const EditCanvasContainer: React.FC<Props> = ({restaurantId, onSaved, onBack}) => {
    const isMobile = useMediaQuery('(max-width:900px)');

    const restaurantQuery = useQuery({
        queryKey: ["restaurant", restaurantId],
        queryFn: () => ResourceService.getResourceById(restaurantId),
        select: (restaurant) => ({
            ...restaurant,
            floorPlanElements: selectRestaurantToFloorPlanElements(restaurant),
        }),
        refetchOnWindowFocus: false,
    });

    const tablesQuery = useQuery({
        queryKey: ['tables', restaurantId],
        queryFn: async () => {
            const response = await ResourceService.getAllResources({
                parentIds: [restaurantId],
                limit: {
                    offset: 0,
                    limit: 1000,
                    sort: [{field: "name", direction: "ASC"}]
                }
            });

            // wait to simulate loading for better UX
            await new Promise(resolve => setTimeout(resolve, 250));
            return response.content || [];
        },
        select: selectTablesToFloorPlanItems,
        refetchOnWindowFocus: false,
    });

    const restaurantServicesQuery = useQuery({
        queryKey: ['floorplanRestaurantServices', restaurantId],
        queryFn: async () => {
            const response = await ServiceService.getAllServices({
                resourceIds: [restaurantId],
                limit: {
                    offset: 0,
                    limit: 1000,
                    sort: [{field: 'name', direction: 'ASC'}],
                },
            });

            return response.content || [];
        },
        refetchOnWindowFocus: false,
    });

    const [restaurantDraft, setRestaurantDraft] = useState<Resource | null>(null);
    const [restaurantServices, setRestaurantServices] = useState<Service[]>([]);
    const [addedRestaurantServices, setAddedRestaurantServices] = useState<Service[]>([]);
    const [removedRestaurantServiceIds, setRemovedRestaurantServiceIds] = useState<string[]>([]);

    // Editor-wide unsaved-changes flag. The canvas reports its own edits through EditCanvasView (only
    // useUndoRedo can distinguish a user edit from the programmatic loading of elements); the sidebar
    // mutations below can't, so their setters are wrapped to mark dirty on the way through the context.
    const [isDirty, setIsDirty] = useState(false);
    const markDirty = useCallback(() => setIsDirty(true), []);

    const setRestaurantDraftDirty = useCallback<typeof setRestaurantDraft>((value) => {
        setRestaurantDraft(value);
        markDirty();
    }, [markDirty]);

    const setAddedRestaurantServicesDirty = useCallback<typeof setAddedRestaurantServices>((value) => {
        setAddedRestaurantServices(value);
        markDirty();
    }, [markDirty]);

    const setRemovedRestaurantServiceIdsDirty = useCallback<typeof setRemovedRestaurantServiceIds>((value) => {
        setRemovedRestaurantServiceIds(value);
        markDirty();
    }, [markDirty]);

    useEffect(() => {
        if (restaurantQuery.data) {
            setRestaurantDraft(restaurantQuery.data);
        }
    }, [restaurantQuery.data]);

    useEffect(() => {
        if (restaurantServicesQuery.data) {
            setRestaurantServices(restaurantServicesQuery.data);
        }
    }, [restaurantServicesQuery.data]);

    useEffect(() => {
        setRemovedRestaurantServiceIds([]);
        setAddedRestaurantServices([]);
    }, [restaurantId]);

    const floorPlanItems = useMemo(
        () => [...(restaurantQuery.data?.floorPlanElements ?? []), ...(tablesQuery.data ?? [])].sort((a, b) => {
            const aZ = a.element?.canvas.zIndex ?? a.table?.canvas.zIndex ?? 0;
            const bZ = b.element?.canvas.zIndex ?? b.table?.canvas.zIndex ?? 0;
            return aZ - bZ;
        }),
        [restaurantQuery.data?.floorPlanElements, tablesQuery.data],
    );
    const originalTableIds = useMemo(
        () => tablesQuery.data?.map((table) => table.id) ?? [],
        [tablesQuery.data],
    );

    const showSeatsAroundTable = restaurantDraft?.meta?.seatsAroundTable ?? restaurantQuery.data?.meta?.seatsAroundTable ?? false;
    const isLoading = restaurantQuery.isLoading || tablesQuery.isLoading;

    if (isLoading || !restaurantDraft) {
        return (
            <Box sx={{height: "100%", width: "100%", display: "flex", flexDirection: "column"}}>
                <EditCanvasHeader onBack={onBack} restaurantName={restaurantQuery.data?.name}/>

                <Box sx={{flex: 1, minHeight: 0}}>
                    <LoadingSkeleton isMobile={isMobile}/>
                </Box>
            </Box>
        );
    }

    return (
        <FloorplanContextProvider mode="edit"
                                  showSeatsAroundTable={showSeatsAroundTable}
                                  restaurantId={restaurantId}
                                  restaurantName={restaurantDraft.name ?? ''}
                                  restaurantDraft={restaurantDraft}
                                  setRestaurantDraft={setRestaurantDraftDirty}
                                  restaurantServices={restaurantServices}
                                  setRestaurantServices={setRestaurantServices}
                                  addedRestaurantServices={addedRestaurantServices}
                                  setAddedRestaurantServices={setAddedRestaurantServicesDirty}
                                  restaurantServicesLoading={restaurantServicesQuery.isLoading}
                                  removedRestaurantServiceIds={removedRestaurantServiceIds}
                                  setRemovedRestaurantServiceIds={setRemovedRestaurantServiceIdsDirty}
                                  refetchRestaurantServices={async () => {
                                      const result = await restaurantServicesQuery.refetch();
                                      return result.data;
                                  }}>
            <EditCanvasInner
                floorPlanItems={floorPlanItems}
                restaurant={restaurantDraft}
                originalTableIds={originalTableIds}
                onSaved={onSaved}
                onBack={onBack}
                isDirty={isDirty}
                setIsDirty={setIsDirty}
            />
        </FloorplanContextProvider>
    );
};

const EditCanvasInner: React.FC<{
    floorPlanItems: any[];
    restaurant: Resource;
    originalTableIds: string[];
    onSaved: () => void;
    onBack: () => void;
    isDirty: boolean;
    setIsDirty: (dirty: boolean) => void;
}> = ({floorPlanItems, restaurant, originalTableIds, onSaved, onBack, isDirty, setIsDirty}) => {

    const {t} = useTranslation();
    const readOnly = useReadOnly();
    const {
        canvas,
        setRestaurantDraft,
        setRestaurantServices,
        updateAllTableElementsLocally,
        restaurantServices,
        addedRestaurantServices,
        removedRestaurantServiceIds,
        setAddedRestaurantServices,
        setRemovedRestaurantServiceIds,
    } = useFloorplanContext();
    const queryClient = useQueryClient();
    const [isSaving, setIsSaving] = useState(false);
    const [activeSaveAction, setActiveSaveAction] = useState<'save' | 'saveAndClose' | null>(null);
    const [closeAfterSave, setCloseAfterSave] = useState(false);
    const savedTableIdsRef = useRef(originalTableIds);
    const savingRef = useRef(false);

    const {showDialog, handleConfirmNavigation, handleCancelNavigation} = useUnsavedChangesWarning({
        hasUnsavedChanges: isDirty && !readOnly,
        isSaving,
    });

    useEffect(() => {
        savedTableIdsRef.current = originalTableIds;
    }, [originalTableIds]);

    useEffect(() => {
        if (!closeAfterSave || isSaving || isDirty) return;

        setCloseAfterSave(false);
        onSaved();
    }, [closeAfterSave, isDirty, isSaving, onSaved]);

    const buildTableGroupRef = (payload: { id?: string; meta?: { canvas?: { id?: string } } }): TableGroupTableRef | null => {
        const tableId = payload.meta?.canvas?.id ?? payload.id;
        const resourceId = payload.id;
        if (!tableId || !resourceId) return null;
        return {tableId, resourceId};
    };

    const handleSave = async (): Promise<boolean> => {
        if (!canvas || !restaurant || savingRef.current) return false;

        savingRef.current = true;
        setIsSaving(true);
        try {
            // Discard ActiveSelection so object coordinates are absolute, not group-relative
            canvas.discardActiveObject();

            const orderedObjects = canvas.getObjects().filter(obj => !!obj.floorPlanItem);

            // Serialize all tables from canvas with current positions
            const tablePayloads = orderedObjects
                .map((obj, zIndex) => serializeTableResource(obj, zIndex))
                .filter((p): p is NonNullable<typeof p> => p !== null);

            // Separate into create vs update based on resource.id
            const createPayloads = tablePayloads
                .filter(t => !t.id)
                .map(t => ({...t, parentId: restaurant.id}));

            const updatePayloads = tablePayloads.filter(t => !!t.id);

            // Detect deleted tables: in original but not in current canvas
            const currentTableIds = new Set(tablePayloads.map(t => t.id).filter(Boolean));
            const tablesToDelete = savedTableIdsRef.current.filter(id => !currentTableIds.has(id));

            // Serialize all elements from canvas with current positions
            const elementsPayload = orderedObjects
                .map((obj, zIndex) => serializeElement(obj, zIndex))
                .filter((e): e is NonNullable<typeof e> => e !== null);

            console.log('Tables to CREATE:', createPayloads);
            console.log('Tables to UPDATE:', updatePayloads);
            console.log('Tables to DELETE:', tablesToDelete);
            console.log('Elements payload:', elementsPayload);

            const halfboardServiceIds = (restaurant.meta?.halfboardServiceIds as string[])
                ?? (restaurant.meta?.serviceIds as string[])
                ?? [];
            const restaurantServiceUpdatePayloads = restaurantServices
                .filter((service) => removedRestaurantServiceIds.includes(service.id ?? ''))
                .map((service) => ({
                    ...service,
                    resourceIds: (service.resourceIds ?? []).filter((resourceId) => resourceId !== restaurant.id),
                }));
            const addedRestaurantServicePayloads = addedRestaurantServices.map((service) => ({
                ...service,
                resourceIds: Array.from(new Set([...(service.resourceIds ?? []), restaurant.id]))
                    .filter((resourceId): resourceId is string => !!resourceId),
            }));
            const createdTableResults = await Promise.allSettled(
                createPayloads.map((payload) => ResourceService.createResource(payload as Resource)),
            );

            const createdResourceByCanvasId = new Map<string, Resource>();
            createdTableResults.forEach((result, index) => {
                if (result.status !== 'fulfilled') return;

                const canvasId = createPayloads[index]?.meta?.canvas?.id
                    ?? (result.value.meta as { canvas?: { id?: string } } | undefined)?.canvas?.id;
                if (canvasId && result.value.id) {
                    createdResourceByCanvasId.set(canvasId, result.value);
                }
            });

            if (createdResourceByCanvasId.size > 0) {
                updateAllTableElementsLocally((item) => {
                    const canvasId = item.table?.canvas.id;
                    if (!canvasId) return item;

                    const createdResource = createdResourceByCanvasId.get(canvasId);
                    if (!createdResource || !item.table) return item;

                    return {
                        ...item,
                        table: {
                            ...item.table,
                            resource: createdResource,
                        },
                    };
                });

                const createdTableIds = Array.from(createdResourceByCanvasId.values())
                    .map((resource) => resource.id)
                    .filter((id): id is string => !!id);
                savedTableIdsRef.current = Array.from(new Set([
                    ...savedTableIdsRef.current,
                    ...createdTableIds,
                ]));
            }

            const failedCreation = createdTableResults.find((result) => result.status === 'rejected');
            if (failedCreation?.status === 'rejected') {
                throw failedCreation.reason;
            }

            const createdTables = createdTableResults.map((result) => {
                if (result.status === 'rejected') throw result.reason;
                return result.value;
            });

            const nextTableGroups = syncTableGroupsWithTables(
                getStoredTableGroups(restaurant.meta as Record<string, any> | undefined),
                [
                    ...updatePayloads
                        .map((payload) => buildTableGroupRef(payload))
                        .filter((table): table is TableGroupTableRef => table !== null),
                    ...createdTables
                        .map((resource, index) => {
                            const tableId = createPayloads[index]?.meta?.canvas?.id
                                ?? (resource.meta as { canvas?: { id?: string } } | undefined)?.canvas?.id;
                            if (!tableId || !resource.id) return null;
                            return {
                                tableId,
                                resourceId: resource.id,
                            };
                        })
                        .filter((table): table is TableGroupTableRef => table !== null),
                ],
            );

            // Exclude floorPlanElements (computed field from select) from the payload
            const {floorPlanElements: _, ...restaurantData} = restaurant as any;
            const {makeAllChildrenBookable: _makeAllChildrenBookable, ...restaurantMeta} = restaurant.meta ?? {};
            const restaurantPayload = {
                ...restaurantData,
                meta: {
                    ...restaurantMeta,
                    elementsV2: elementsPayload,
                    tableGroups: nextTableGroups,
                    isTemporary: false,
                },
            };

            console.log('Restaurant payload:', restaurantPayload);

            const saveResults = await Promise.allSettled([
                ...updatePayloads.map(p => ResourceService.updateResource(p as Resource)),
                ...restaurantServiceUpdatePayloads.map((service) => ServiceService.updateService(service)),
                ...addedRestaurantServicePayloads.map((service) => ServiceService.updateService(service)),
                ...(tablesToDelete.length > 0
                    ? [ResourceService.deleteResource({id: tablesToDelete})]
                    : []),
                ResourceService.updateResource(restaurantPayload as Resource),
                FloorplanService.updateHalfboardServices({halfboardServiceIds}),
                new Promise(resolve => setTimeout(resolve, 1000)),
            ]);

            const failedSave = saveResults.find((result) => result.status === 'rejected');
            if (failedSave?.status === 'rejected') {
                throw failedSave.reason;
            }

            const nextSavedTableIds = tablePayloads
                .map((payload) => {
                    if (payload.id) return payload.id;
                    return createdResourceByCanvasId.get(payload.meta?.canvas?.id ?? '')?.id;
                })
                .filter((id): id is string => !!id);

            savedTableIdsRef.current = nextSavedTableIds;
            setAddedRestaurantServices([]);
            setRemovedRestaurantServiceIds([]);
            if (restaurantServiceUpdatePayloads.length > 0 || addedRestaurantServicePayloads.length > 0) {
                const removedIds = new Set(restaurantServiceUpdatePayloads.map((service) => service.id).filter(Boolean));
                const addedById = new Map(
                    addedRestaurantServicePayloads
                        .filter((service) => !!service.id)
                        .map((service) => [service.id as string, service]),
                );
                setRestaurantServices((currentServices) => {
                    const nextServices = currentServices.map((service) => {
                        if (removedIds.has(service.id)) {
                            return {
                                ...service,
                                resourceIds: (service.resourceIds ?? []).filter((resourceId) => resourceId !== restaurant.id),
                            };
                        }

                        return addedById.get(service.id ?? '') ?? service;
                    });

                    addedRestaurantServicePayloads.forEach((service) => {
                        if (!nextServices.some((currentService) => currentService.id === service.id)) {
                            nextServices.push(service);
                        }
                    });

                    return nextServices;
                });
            }
            setRestaurantDraft((currentDraft) => {
                if (!currentDraft) return currentDraft;

                return {
                    ...currentDraft,
                    ...restaurantData,
                    meta: restaurantPayload.meta,
                };
            });

            // Keep active editor queries mounted until Save & Close can navigate. Mark them stale
            // without refetching so the next visit still loads fresh server state.
            queryClient.invalidateQueries(['restaurant', restaurant.id], {refetchActive: false});
            queryClient.invalidateQueries(['tables', restaurant.id], {refetchActive: false});
            queryClient.invalidateQueries(['floorplanRestaurantServices', restaurant.id], {refetchActive: false});

            // Reader queries are inactive while editing and can be cleared immediately.
            queryClient.removeQueries(['floorplanTables', restaurant.id]);
            queryClient.removeQueries(['floorplanAssigned', restaurant.id]);
            queryClient.removeQueries(['floorplanUnassigned', restaurant.id]);
            queryClient.removeQueries(['floorplanHalfboardProfiles', restaurant.id]);
            // Clear the root resources cache — the restaurant object (with meta.elementsV2)
            // is cached here and passed as a prop to the read view.
            queryClient.removeQueries(['allRootResourcesForSector']);
            queryClient.invalidateQueries('allServices', {exact: false});

            // Last write wins over the markDirty() the setters above trigger, and it lands before
            // handleSaveAndClose awaits us and navigates — so a saved floorplan never prompts.
            setIsDirty(false);

            return true;
        } catch (error) {
            console.error('Failed to save floorplan:', error);
            enqueueSnackbar(t("Failed to save floorplan"), {variant: 'error'});
            // Invalidate queries to resync canvas with server state after partial failure
            await queryClient.invalidateQueries(['restaurant', restaurant.id]);
            await queryClient.invalidateQueries(['tables', restaurant.id]);
            return false;
        } finally {
            savingRef.current = false;
            setIsSaving(false);
        }
    };

    const handleSaveOnly = async () => {
        setActiveSaveAction('save');
        try {
            await handleSave();
        } finally {
            setActiveSaveAction(null);
        }
    };

    const handleSaveAndClose = async () => {
        setActiveSaveAction('saveAndClose');
        try {
            const success = await handleSave();
            if (success) setCloseAfterSave(true);
        } finally {
            setActiveSaveAction(null);
        }
    };

    return (
        <Box sx={{height: "100%", width: "100%", display: "flex", flexDirection: "column"}}>
            <EditCanvasHeader
                onBack={onBack}
                restaurantName={restaurant.name}
                showActions={true}
                onSaveOnly={handleSaveOnly}
                onSaveAndClose={handleSaveAndClose}
                activeSaveAction={activeSaveAction}
                isSaving={isSaving}
                readOnly={readOnly}
                isDirty={isDirty}
            />

            <Box sx={{flex: 1, minHeight: 0}}>
                <EditCanvasView
                    floorPlanItems={floorPlanItems}
                    isSaving={isSaving}
                    onDirtyChange={setIsDirty}
                />
            </Box>

            <UnsavedChangesDialog
                open={showDialog}
                onConfirm={handleConfirmNavigation}
                onCancel={handleCancelNavigation}
            />



        </Box>
    );
};

export default EditCanvasContainer;
